import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { formatPhone } from '@dorixona/utils'
import { DetailHead } from '../../ui/DetailHead'
import { Icon } from '../../ui/Icon'
import { TimePicker } from '../../ui/DatePicker'
import { MapPicker } from '../../ui/MapPicker'
import { Failed, Loading, failureText } from '../../ui/Load'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { useSession } from '../auth/session'
import { useSaveStore, useStores, type DayHours, type Store } from './api'
import { WEEKDAYS, newStore } from './model'

export interface StoreEditScreenProps {
  onToast: (message: string) => void
}

/** Часы рабочего дня по умолчанию — когда день включают в форме. */
const DEFAULT_DAY: DayHours = { open: '08:00', close: '22:00' }

/** Координата из поля: пусто — нет координаты, мусор — тоже нет. */
function coord(value: string): number | null {
  const n = Number(value.replace(',', '.'))
  return value.trim() === '' || Number.isNaN(n) ? null : n
}

/** Пустая аптека для формы создания: выключена, неделя закрыта, только самовывоз. */
const BLANK = newStore({ name: '', district: '', address: '' })

/**
 * Правка аптеки — отдельной страницей (`/stores/:id/edit`): данные, карта,
 * услуги и неделя часов в окне не помещались. Сервер принимает аптеку
 * целиком (`PUT`). Новая аптека (`/stores/new`) заводится той же формой и
 * сразу целиком (`POST`), выключенной — включают её в карточке.
 */
export function StoreEditScreen({ onToast }: StoreEditScreenProps) {
  const navigate = useNavigate()
  const params = useParams()
  const creating = params.id === undefined
  const id = Number(params.id)
  const { can } = useSession()
  const stores = useStores({ enabled: !creating })

  if (creating) {
    if (can('stores', 'c')) return <StoreEdit store={BLANK} creating onToast={onToast} />
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-pin" />
          <b>Добавлять аптеки роль не разрешает</b>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/stores')
            }}
          >
            К списку аптек
          </button>
        </div>
      </div>
    )
  }
  if (stores.isPending) return <Loading what="аптеку" />
  if (stores.isError) {
    return (
      <Failed
        what="аптеку"
        error={stores.error}
        onRetry={() => {
          void stores.refetch()
        }}
      />
    )
  }
  const store = stores.data.find((s) => s.id === id)
  if (!store || !can('stores', 'u')) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-pin" />
          <b>{store ? 'Править аптеку роль не разрешает' : 'Аптека не найдена'}</b>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate(store ? `/stores/${store.id}` : '/stores')
            }}
          >
            {store ? 'К аптеке' : 'К списку аптек'}
          </button>
        </div>
      </div>
    )
  }
  return <StoreEdit key={store.id} store={store} onToast={onToast} />
}

interface StoreEditProps {
  store: Store
  /** Новая аптека: назад — к списку, «Сохранить» её заводит. */
  creating?: boolean
  onToast: (message: string) => void
}

/**
 * Форма загруженной аптеки. Доставка идёт из ближайшей аптеки, поэтому без
 * координат её не включить — сервер откажет, и форма говорит об этом раньше.
 */
function StoreEdit({ store, creating = false, onToast }: StoreEditProps) {
  const navigate = useNavigate()
  const save = useSaveStore()
  // Телефон — в привычном виде «+998 71 200-00-00», а не строкой цифр.
  const [draft, setDraft] = useState({
    ...store,
    phone: store.phone ? formatPhone(store.phone) : '',
  })
  const [lat, setLat] = useState(store.lat === null ? '' : String(store.lat))
  const [lng, setLng] = useState(store.lng === null ? '' : String(store.lng))
  const [error, setError] = useState('')
  const [dirty, setDirty] = useState(false)
  // Пустое обязательное поле краснеет, только когда из него ушли: новая
  // аптека открывается пустой, и вся форма в красном выглядела бы ошибкой.
  // У правки поля заполнены, поэтому там краснеет и стёртое на ходу.
  const [left, setLeft] = useState<ReadonlySet<string>>(new Set())
  useUnsavedWarning(dirty)
  const back = () => {
    void navigate(creating ? '/stores' : `/stores/${store.id}`)
  }

  const edit = (patch: Partial<Store>) => {
    setDraft({ ...draft, ...patch })
    setDirty(true)
  }
  const editCoord = (setter: (value: string) => void, value: string) => {
    setter(value)
    setDirty(true)
  }
  const setDay = (index: number, day: DayHours | null) => {
    edit({ hours: draft.hours.map((d, i) => (i === index ? day : d)) })
  }

  const missing = [draft.name, draft.district, draft.address].some((v) => v.trim() === '')
  const noCoords = draft.acceptsDelivery && (coord(lat) === null || coord(lng) === null)

  const problem = missing
    ? 'Укажите название, район и адрес'
    : noCoords
      ? 'Для доставки нужны координаты аптеки'
      : ''

  function submit() {
    setError('')
    save.mutate(
      {
        ...draft,
        name: draft.name.trim(),
        district: draft.district.trim(),
        address: draft.address.trim(),
        metro: draft.metro.trim(),
        phone: draft.phone.trim(),
        manager: draft.manager.trim(),
        lat: coord(lat),
        lng: coord(lng),
      },
      {
        onSuccess: (saved) => {
          setDirty(false)
          onToast(
            creating ? 'Аптека добавлена — включите её в карточке' : 'Данные аптеки сохранены',
          )
          void navigate(`/stores/${saved.id}`)
        },
        onError: (failure) => {
          setError(failureText(failure))
        },
      },
    )
  }

  const text = (
    id: string,
    label: string,
    key: 'name' | 'district' | 'address' | 'metro' | 'phone' | 'manager',
    extra: { required?: boolean; placeholder?: string; tel?: boolean } = {},
  ) => (
    <div className="ui-field">
      <label className="ui-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="ui-input"
        type={extra.tel ? 'tel' : 'text'}
        placeholder={extra.placeholder}
        aria-invalid={
          extra.required && draft[key].trim() === '' && (!creating || left.has(key))
            ? true
            : undefined
        }
        value={draft[key]}
        onChange={(e) => {
          edit({ [key]: e.target.value })
        }}
        onBlur={() => {
          if (!left.has(key)) setLeft(new Set(left).add(key))
        }}
      />
    </div>
  )

  /** Услуга аптеки — карточкой-флажком: название и что оно значит для заказа. */
  const option = (
    key: 'acceptsPickup' | 'acceptsDelivery' | 'dispensesRx',
    title: string,
    note: string,
  ) => (
    <label className="sf-option">
      <input
        type="checkbox"
        checked={draft[key]}
        onChange={(e) => {
          edit({ [key]: e.target.checked })
        }}
      />
      <b>{title}</b>
      <span>{note}</span>
    </label>
  )

  const MAP = (
    <>
      {/* Поиск, «моё местоположение» и щелчок по карте ставят точку и заполняют
          координаты; ввод чисел двигает точку — все пути ведут к одним полям. */}
      <MapPicker
        label="Точка аптеки на карте: щёлкните, чтобы выбрать"
        point={
          coord(lat) !== null && coord(lng) !== null
            ? { lat: coord(lat) as number, lng: coord(lng) as number }
            : null
        }
        onPick={(point) => {
          editCoord(setLat, String(point.lat))
          editCoord(setLng, String(point.lng))
        }}
      />
      <div className="ui-form-row">
        <div className="ui-field">
          <label className="ui-label" htmlFor="sf-lat">
            Широта
          </label>
          <input
            id="sf-lat"
            className="ui-input"
            inputMode="decimal"
            placeholder="41.2995"
            aria-invalid={noCoords ? true : undefined}
            value={lat}
            onChange={(e) => {
              editCoord(setLat, e.target.value)
            }}
          />
        </div>
        <div className="ui-field">
          <label className="ui-label" htmlFor="sf-lng">
            Долгота
          </label>
          <input
            id="sf-lng"
            className="ui-input"
            inputMode="decimal"
            placeholder="69.2401"
            aria-invalid={noCoords ? true : undefined}
            value={lng}
            onChange={(e) => {
              editCoord(setLng, e.target.value)
            }}
          />
        </div>
      </div>
      <span className="ui-hint">Координаты нужны для доставки</span>
    </>
  )

  const HOURS = (
    <>
      <div className="ui-seg" role="group" aria-label="Режим работы">
        <button
          type="button"
          aria-pressed={draft.is247}
          onClick={() => {
            edit({ is247: true })
          }}
        >
          Круглосуточно
        </button>
        <button
          type="button"
          aria-pressed={!draft.is247}
          onClick={() => {
            edit({ is247: false })
          }}
        >
          По расписанию
        </button>
      </div>
      {draft.is247 ? null : (
        <div className="sf-week">
          {WEEKDAYS.map((name, i) => {
            // У формы всегда семь дней (toStore их дополняет).
            const day = draft.hours[i] as DayHours | null
            return (
              <div className="sf-day" key={name} data-off={day ? undefined : true}>
                <label className="ui-check">
                  <input
                    type="checkbox"
                    checked={day !== null}
                    onChange={(e) => {
                      setDay(i, e.target.checked ? DEFAULT_DAY : null)
                    }}
                  />
                  {name}
                </label>
                {day ? (
                  <>
                    <TimePicker
                      label={`${name}: открывается`}
                      value={day.open}
                      onChange={(open) => {
                        setDay(i, { ...day, open })
                      }}
                    />
                    <span aria-hidden="true">–</span>
                    <TimePicker
                      label={`${name}: закрывается`}
                      value={day.close}
                      onChange={(close) => {
                        setDay(i, { ...day, close })
                      }}
                    />
                  </>
                ) : (
                  <span className="sf-off">выходной</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )

  const card = (id: string, title: string, children: ReactNode) => (
    <section className="ui-card" aria-labelledby={id}>
      <div className="ui-card-head">
        <h3 id={id}>{title}</h3>
      </div>
      <div className="ui-card-body ui-form">{children}</div>
    </section>
  )

  return (
    <>
      <DetailHead
        backLabel={creating ? 'Аптеки' : store.name}
        onBack={back}
        title={creating ? 'Новая аптека' : 'Изменить аптеку'}
        sub={creating ? null : <span>{store.address}</span>}
      />

      <div className="ui-detail-col sf-grid">
        {card(
          'sf-main',
          'Аптека',
          <>
            <div className="ui-form-row">
              {text('sf-name', 'Название', 'name', {
                required: true,
                placeholder: 'Например, 03 Dorixona Юнусабад',
              })}
              {text('sf-district', 'Район', 'district', {
                required: true,
                placeholder: 'Например, Юнусабад',
              })}
            </div>
            <div className="ui-form-row">
              {text('sf-address', 'Адрес', 'address', {
                required: true,
                placeholder: 'Улица и дом',
              })}
              {text('sf-metro', 'Ближайшее метро', 'metro', { placeholder: 'Если есть рядом' })}
            </div>
          </>,
        )}
        {/* Карта — на всю ширину формы, сразу под адресом: точку ищут по нему. */}
        {card('sf-map', 'Расположение', MAP)}
        {card(
          'sf-contacts',
          'Контакты',
          <div className="ui-form-row">
            {text('sf-phone', 'Телефон', 'phone', {
              tel: true,
              placeholder: '+998 71 200-00-00',
            })}
            {text('sf-manager', 'Заведующий', 'manager', { placeholder: 'Имя и фамилия' })}
          </div>,
        )}
        {card(
          'sf-does',
          'Что делает аптека',
          <div className="sf-options">
            {option('acceptsPickup', 'Самовывоз', 'Клиент забирает заказ сам')}
            {option('acceptsDelivery', 'Доставка', 'Курьер везёт из этой аптеки')}
            {option('dispensesRx', 'Отпуск по рецепту', 'Есть лицензия на рецептурные')}
          </div>,
        )}
        {card('sf-hours', 'Часы работы', HOURS)}
      </div>

      <div className="ui-savebar" data-dirty={dirty || undefined}>
        <span className="state" role="status">
          {error ||
            // Новую аптеку сразу говорит, чего не хватает; правка — после первой буквы.
            ((creating || dirty) && problem) ||
            (dirty ? 'Есть несохранённые изменения' : 'Все изменения сохранены')}
        </span>
        <button type="button" className="ui-btn" data-variant="secondary" onClick={back}>
          {dirty ? 'Отменить' : creating ? 'К списку' : 'К аптеке'}
        </button>
        <button
          type="button"
          className="ui-btn"
          disabled={!dirty || problem !== '' || save.isPending}
          aria-busy={save.isPending}
          onClick={submit}
        >
          {creating ? 'Добавить аптеку' : 'Сохранить'}
        </button>
      </div>
    </>
  )
}
